import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined, ReloadOutlined, LoadingOutlined } from '@ant-design/icons';
import DatabaseCard from './components/DatabaseCard';
import EmptyState from '../../components/shared/EmptyState/EmptyState';
import { servicesService } from '../../services';
import { databaseEngines } from '../../constants/databaseEngine';
import { getServiceAge } from '../../utils/serviceHelpers';
import { useTranslation } from 'react-i18next';

type CardStatus = 'running' | 'stopped' | 'deploying' | 'error';

const mapServiceStatusToCard = (status: string): CardStatus => {
  const s = (status || '').toLowerCase();
  if (s === 'running') return 'running';
  if (s === 'stopped') return 'stopped';
  if (['error', 'terminated', 'deleted', 'failed'].includes(s)) return 'error';
  return 'deploying';
};

const mapServiceToDatabase = (svc: any) => {
  const dbConfig = svc.metadata?.databaseConfig || {};
  const dbInfo = svc.dbInfo || {};
  const engineName = dbConfig.engineName || '';
  const engineParts = engineName.split(' ');
  // Resolver el nombre del motor desde su id real (sqlserver-web, postgres,
  // mongodb...) contra el catálogo, para no truncar "SQL Server" a "SQL"
  // (que además hacía que el icono cayera en PostgreSQL).
  const rawEngineId = (dbConfig.engine || svc.engine || '').toLowerCase();
  const engineMeta = rawEngineId
    ? databaseEngines.find(
        (e) => rawEngineId === e.id || rawEngineId.startsWith(e.id) || e.id.startsWith(rawEngineId)
      )
    : undefined;
  const engine = engineMeta?.name || engineParts[0] || rawEngineId || '—';
  const version = dbConfig.version || dbInfo.engineVersion || engineParts.slice(1).join(' ') || '—';
  return {
    id: svc.id,
    name: svc.name || dbConfig.name || svc.dbName || '—',
    status: mapServiceStatusToCard(svc.status),
    engine,
    version,
    instanceType: svc.dbInstanceClass || dbInfo.instanceClass || dbConfig.instanceType || '—',
    region: svc.region || '—',
    createdAt: getServiceAge(svc.createdAt),
  };
};

const Databases: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [databases, setDatabases] = useState<ReturnType<typeof mapServiceToDatabase>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'running' | 'stopped' | 'deploying' | 'error' | 'all'>('all');

  const fetchDatabases = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await servicesService.getByCategory('DATABASE');
      const services = (response.services || []).filter(
        (s: any) => (s.type || s.category || '').toUpperCase() === 'DATABASE'
      );
      setDatabases(services.map(mapServiceToDatabase));
    } catch (err: any) {
      setError(err?.message || 'Error loading databases');
      setDatabases([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatabases();
  }, []);

  const filteredDatabases = databases.filter((db) => {
    const matchesSearch =
      db.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      db.engine.toLowerCase().includes(searchTerm.toLowerCase()) ||
      db.version.toLowerCase().includes(searchTerm.toLowerCase()) ||
      db.instanceType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      db.region.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || db.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const STATUS_PILLS = [
    { value: 'all', label: t('common.all') || 'Todas' },
    { value: 'running', label: 'Running' },
    { value: 'stopped', label: 'Stopped' },
    { value: 'deploying', label: 'Deploying' },
    { value: 'error', label: 'Error' },
  ];

  return (
    <div className="max-w-[1600px] mx-auto px-8 py-4">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Databases</h1>
          {!loading && (
            <span className="text-sm text-gray-400 dark:text-gray-500">{filteredDatabases.length}</span>
          )}
        </div>

        <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
          {/* Search */}
          <div className="relative">
            <SearchOutlined className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name, engine or region..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-900 dark:focus:ring-gray-400 w-48"
            />
          </div>

          {/* Status pills */}
          <div className="flex items-center gap-1">
            {STATUS_PILLS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setFilterStatus(value as typeof filterStatus)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  filterStatus === value
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-gray-900 dark:border-white'
                    : 'text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-400'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Refresh */}
          <button
            onClick={fetchDatabases}
            disabled={loading}
            className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-40"
            title={t('common.refresh')}
          >
            {loading ? <LoadingOutlined spin /> : <ReloadOutlined />}
          </button>

          {/* Create */}
          <button
            onClick={() => navigate('/resources/create-database')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm rounded-md hover:bg-gray-700 dark:hover:bg-gray-100 transition-colors"
          >
            <PlusOutlined className="text-xs" />
            New Database
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-20">
          <LoadingOutlined className="text-2xl text-gray-300 dark:text-gray-600" spin />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-3 py-20">
          <p className="text-sm text-red-500">{error}</p>
          <button
            onClick={fetchDatabases}
            className="px-3 py-1.5 text-sm bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-md hover:bg-gray-700"
          >
            Retry
          </button>
        </div>
      ) : filteredDatabases.length === 0 ? (
        <EmptyState
          title="No databases found"
          description={
            searchTerm || filterStatus !== 'all'
              ? "Try adjusting your search or filters."
              : "Create your first managed database to get started."
          }
          action={
            !searchTerm && filterStatus === 'all'
              ? { label: 'New Database', onClick: () => navigate('/resources/create-database') }
              : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDatabases.map((db) => (
            <DatabaseCard
              key={db.id}
              id={db.id}
              name={db.name}
              status={db.status}
              engine={db.engine}
              version={db.version}
              instanceType={db.instanceType}
              region={db.region}
              createdAt={db.createdAt}
              onRefresh={fetchDatabases}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Databases;
